import React from 'react';
import styled from 'styled-components';
import { getFaviconUrl, handleFaviconError } from './Utils';

interface CitationListProps {
  citations: any;
}

const CitationList: React.FC<CitationListProps> = ({ citations }) => {
  const extractTextFromUrl = (citationText: string): string => {
    if (!citationText) return '';

    try {
      // Check if it's a URL with text parameter
      if (citationText.includes('text=')) {
        const url = new URL(citationText);
        // Fixed regex to capture text until next parameter or end of fragment
        const textParam = url.hash.match(/text=([^&]+)/);
        if (textParam && textParam[1]) {
          // Decode and clean up the text properly
          const decodedText = decodeURIComponent(textParam[1]).replace(/%20/g, ' ').trim();
          return decodedText.length > 35 ? decodedText.substring(0, 35) + ' ...' : decodedText;
        }
        return '';
      }

      // Fallback: extract title-like content from URL or return as-is
      const cleanText = citationText.replace(/https?:\/\/[^\s]+/g, '').trim();
      return cleanText || citationText;
    } catch (error) {
      return citationText;
    }
  };

  return (
    <StyledCitations>
      <h4 className='citations-title'>Citations</h4>
      <ul>
        {citations?.map((c: any, idx: number) => {
          const domain = c.url ? new URL(c.url).hostname.replace('www.', '') : '';
          const favicon = c.url ? getFaviconUrl({ url: c.url }) : '';
          return (
            <li key={c.url || idx} className='citation-item activity'>
              <a className='citation-link' href={c.url} target='_blank' rel='noopener noreferrer'>
                <div className='citation-header'>
                  {favicon && (
                    <span className='activity-icon'>
                      <img
                        className='citation-favicon'
                        src={favicon}
                        alt={domain}
                        onError={handleFaviconError}
                      />
                    </span>
                  )}
                  <span className='citation-domain'>{domain}</span>
                </div>
                <div className='citation-title'>
                  <span className='citation-description'>{c.text}</span>
                  <br />
                  <span className='citation-url-text'>{extractTextFromUrl(c.url)}</span>
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </StyledCitations>
  );
};

const StyledCitations = styled.div`
  .citation-item {
    margin-bottom: 5px;
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 5px;
    overflow: hidden;
    color: #1a1a24;
    text-overflow: ellipsis;
    font-family: 'EYInterstate';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
  .citation-item:hover {
    border-radius: 8px;
    background-color: #f5f5f5;
  }
  .citation-header {
    display: flex;
    font-size: 0.98rem;
    color: #4e4e5d;
    margin-bottom: 2px;
  }
  .citation-domain {
    font-weight: 400;
    color: #4e4e5d;
    font-family: 'ey_interstate';
    font-size: 12px;
    vertical-align: middle;
    line-height: 20px;
    max-width: 95%;
  }
  .citation-title {
    font-size: 12px;
    margin-bottom: 2px;
    margin-top: 2px;
    color: #2e2e38;
    font-weight: 300;
    font-family: 'ey_interstate';
  }
  .citation-title a {
    color: #222;
    text-decoration: none;
  }
  .citation-title a:hover {
    text-decoration: underline;
  }
  .citation-snippet {
    color: #444;
    font-size: 0.98rem;
    margin-top: 2px;
  }
  .citation-favicon img {
    max-width: 16px;
    vertical-align: middle;
    background: #f3f3f3;
  }
  .citations-title {
    margin: 0px;
    padding: 10px 0px 0px 20px;
    color: #4e4e5d;
    font-family: 'EYInterstate';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }

  .citation-link {
    word-break: break-word;
    text-decoration: none !important;
  }
  .citation-url-text {
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: EYInterstate;
    font-size: 12px;
    font-style: normal;
    font-weight: 300;
    line-height: 24px;
  }
  .citation-description {
    overflow: hidden;
    color: #1a1a24;
    text-overflow: ellipsis;
    font-family: EYInterstate;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
`;

export default CitationList;
